import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useGlobalQuery } from 'state/query-client/hooks';
import { useConfig } from 'hooks/use-config';
import { scheduledJobsService } from '../services/scheduled-jobs.service';
import type {
  GetScheduledJobsPayload,
  GetScheduledJobsResponse,
  ScheduledJobRunListResponse,
  SchedulePreviewResponse,
  ScheduleSpecDto,
  UpdateScheduledJobPayload,
} from '../services/scheduled-jobs.types';

export const useGetScheduledJobs = (
  payload: GetScheduledJobsPayload = {},
  queryOptions?: { enabled?: boolean }
) => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';

  return useGlobalQuery<GetScheduledJobsResponse>({
    queryKey: ['scheduled-jobs', payload],
    queryFn: () => scheduledJobsService.getScheduledJobs(payload, blocksKey),
    enabled: !!blocksKey && (queryOptions?.enabled ?? true),
    // Refetch whenever the page is opened; useGlobalQuery disables mount refetches by default.
    refetchOnMount: 'always',
    staleTime: 0,
  });
};

export const useDeleteScheduledJob = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['scheduled-jobs', 'delete'],
    mutationFn: (jobId: string) =>
      scheduledJobsService.deleteScheduledJob(jobId, blocksKey, {
        deleteConversation: false,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-jobs'] });
    },
  });
};

export const useUpdateScheduledJob = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['scheduled-jobs', 'update'],
    mutationFn: ({ jobId, payload }: { jobId: string; payload: UpdateScheduledJobPayload }) =>
      scheduledJobsService.updateScheduledJob(jobId, payload, blocksKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-jobs'] });
    },
  });
};

export const usePauseScheduledJob = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['scheduled-jobs', 'pause'],
    mutationFn: (jobId: string) => scheduledJobsService.pauseScheduledJob(jobId, blocksKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-jobs'] });
    },
  });
};

export const useResumeScheduledJob = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['scheduled-jobs', 'resume'],
    mutationFn: (jobId: string) => scheduledJobsService.resumeScheduledJob(jobId, blocksKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-jobs'] });
    },
  });
};

export const useRunScheduledJobNow = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['scheduled-jobs', 'run-now'],
    mutationFn: (jobId: string) => scheduledJobsService.runScheduledJobNow(jobId, blocksKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-jobs'] });
    },
  });
};

/** Live preview of a candidate schedule — no job required. `spec: null` while unbuildable. */
export const useSchedulePreview = (spec: ScheduleSpecDto | null) => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';

  return useGlobalQuery<SchedulePreviewResponse>({
    queryKey: ['scheduled-jobs', 'preview', spec],
    queryFn: () => scheduledJobsService.previewSchedule({ ...(spec as ScheduleSpecDto), count: 3 }, blocksKey),
    enabled: !!blocksKey && !!spec,
    staleTime: 0,
  });
};

export const useScheduledJobRuns = (jobId: string, queryOptions?: { enabled?: boolean }) => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';

  return useGlobalQuery<ScheduledJobRunListResponse>({
    queryKey: ['scheduled-jobs', jobId, 'runs'],
    queryFn: () => scheduledJobsService.getScheduledJobRuns(jobId, blocksKey, 5),
    enabled: !!blocksKey && !!jobId && (queryOptions?.enabled ?? true),
    staleTime: 0,
  });
};
